import api from '@/api'
import config from '@/config'
import { DataFilters, KpisConnectionReportDataUserIndicator, KpisGroupIndicatorsBySpecificTime, KpiUserProductivityDate, KpiUserProductivity, KpisProductivity, KpisStatusManagement, KpisStatusManagementData, KpisConnectionReportData, KpisConnectionReportDataUsers, KpisGroupIndicatorsByTime, CapacityProductivityResponse } from '@/types/Dashboard'

// Test data
// import data from '@/data/RptConexion'
import moment from 'moment'
import { TrackingUserGroup } from '@/types/Tracking'

const TIME_FORMAT = new RegExp(/^\d{1,2}:\d{1,2}:\d{1,2}$/)

class ReportsService {
  async getCapacidadProductividad (filters: DataFilters): Promise<CapacityProductivityResponse> {
    const params = new URLSearchParams()
    params.append('template', filters.template.toString())
    params.append('group', filters.group.toString())
    params.append('start', filters.initDate)
    params.append('end', filters.endDate)
    params.append('timezone', (moment().utcOffset() / 60).toString())
    const response = await api.get(`${config.apiReportsUrl}capacity-and-productivity`, { params })
    const data = response.data
    data.dates.forEach((it: any) => {
      it._dateFormatted = moment(it.date, 'YYYY-MM-DD')
      it.users.forEach((it2: KpiUserProductivity) => {
        it2._workedHoursNumber = this.getHoursNumberFromTime(it2.worked_hours)
      })
    })
    return data
  }

  async getEstatusGestion (filters: DataFilters): Promise<KpisStatusManagementData> {
    const params = new URLSearchParams()
    params.append('template', filters.template.toString())
    params.append('group', filters.group.toString())
    params.append('date', filters.endDate)
    params.append('timezone', (moment().utcOffset() / 60).toString())
    const response = await api.get(`${config.apiReportsUrl}supervision`, { params })
    const data = response.data
    if (data.users) {
      data.users.forEach((it: any) => {
        it.isWorkingUser = it.pending_management > 0 && it.managed > 0
        it.isNotWorkingUser = it.pending_management > 0 && it.managed === 0
        it.isNotAssignedUser = it.pending_management === 0
      })
    }
    return data as KpisStatusManagementData
  }

  async getReporteConexion (filters: DataFilters): Promise<KpisConnectionReportData[]> {
    const params = new URLSearchParams()
    params.append('group', filters.group.toString())
    params.append('date', filters.endDate)
    params.append('timezone', (moment().utcOffset() / 60).toString())
    // let dataResult = data as any // Test
    const response = await api.get(`${config.apiReportsUrl}connection`, { params })
    const dataResult = response.data as any

    const dataResultKeys = Object.keys(dataResult)
    const kpisDataSource: KpisConnectionReportData[] = []
    let dsItem

    for (const key of dataResultKeys) {
      dsItem = new KpisConnectionReportData()
      dsItem.groupId = key
      dsItem.totalUsers = dataResult[key].total
      dsItem.users = dataResult[key].users as KpisConnectionReportDataUsers[]
      kpisDataSource.push(dsItem)
    }

    return kpisDataSource
  }

  async postSupportBilling (detail: number, email: string, client: number, domain: number, month: number) {
    const paramsConfig = {
      email,
      client: String(client),
      domain: String(domain),
      detail: String(detail === 2)
    }
    const params = new URLSearchParams(paramsConfig)

    const endpoint = month === 1 ? `billing/support` : `billing/support-daily`
    const url = `${config.apiReportsUrl}${endpoint}?${params.toString()}`

    return api.post(url)
  }

  async postDailyBilling (email: string, reportBilling: number) {
    const isGeneral = reportBilling === 1

    const params = new URLSearchParams({
      email,
      ...(isGeneral ? { day_billing: `1` } : { period: `DAILY` })
    })

    const endpoint = isGeneral ? `billing/daily` : `billing/infonavit`
    const url = `${config.apiReportsUrl}${endpoint}?${params.toString()}`

    return api.post(url)
  }

  getGroupProductivityKpis (group: TrackingUserGroup, data: KpiUserProductivityDate[], totalUsers: number, date?: string): KpisProductivity {
    const kpis = new KpisProductivity()

    kpis.groupId = group.id
    kpis.groupName = group.name
    kpis.totalUsers = totalUsers

    const usersArr: string[] = []
    for (const it of data) {
      if (!date || date === it.date) {
        kpis.date = date ? it._dateFormatted.format('MMM D') : ''
        for (const it2 of it.users) {
          kpis.workedTasksCount += it2.worked_amount
          if (usersArr.indexOf(it2.user) === -1) {
            usersArr.push(it2.user)
            kpis.usersWithAtLeastOneTask++
          }
        }
      }
    }

    // Users who worked
    kpis.precentageUsersWhoWorked = totalUsers ? Number((kpis.usersWithAtLeastOneTask / totalUsers * 100).toFixed(1)) : 0
    // Average worked tasks
    kpis.averageWorkedTasks = kpis.usersWithAtLeastOneTask ? Number((kpis.workedTasksCount / kpis.usersWithAtLeastOneTask).toFixed(1)) : 0
    // Average worked hours
    let days, hoursSum, worked
    for (const user of usersArr) {
      days = 0
      hoursSum = 0
      for (const it1 of data) {
        if (!date || date === it1.date) {
          worked = false
          for (const it2 of it1.users) {
            if (user === it2.user) {
              worked = true
              hoursSum += it2._workedHoursNumber
              break
            }
          }
          if (worked) {
            days++
          }
        }
      }
      kpis.workedHoursCount += days ? hoursSum / days : 0
    }
    kpis.avegareWorkedHours = kpis.usersWithAtLeastOneTask ? Number((kpis.workedHoursCount / kpis.usersWithAtLeastOneTask).toFixed(1)) : 0
    return kpis
  }

  getStatusManagementKpis (data: KpisStatusManagementData): KpisStatusManagement {
    const kpis = new KpisStatusManagement()
    kpis.unassignedTasks = data.unassigned
    kpis.sumManagedTasks = data.users.reduce((sum, it) => sum + it.managed, 0)
    kpis.sumManagedTasksToday = data.users.reduce((sum, it) => sum + it.managed_today, 0)
    kpis.sumPendingManagement = data.users.reduce((sum, it) => sum + it.pending_management, 0)
    kpis.sumActiveUsers = data.users.reduce((count, it) => count + (it.managed > 0 ? 1 : 0), 0)
    kpis.sumWorkingUsers = data.users.reduce((count, it) => count + (it.isWorkingUser ? 1 : 0), 0)
    kpis.sumNotWorkingUsers = data.users.reduce((count, it) => count + (it.isNotWorkingUser ? 1 : 0), 0)
    kpis.sumNotAssignedUsers = data.users.reduce((count, it) => count + (it.isNotAssignedUser ? 1 : 0), 0)
    kpis.totalTasks = kpis.sumManagedTasks + kpis.sumPendingManagement + (kpis.unassignedTasks || 0)
    kpis.totalUsers = data.users.length
    return kpis
  }

  getGroupIndicatorsByTimeKpis (data: KpisConnectionReportData) {
    const groupIndicator = new KpisGroupIndicatorsByTime()
    groupIndicator.groupName = data.groupName
    groupIndicator.totalUsers = data.totalUsers

    for (let i = 0; i < 24; i++) {
      const itemHour = groupIndicator[i as keyof KpisGroupIndicatorsByTime] as KpisGroupIndicatorsBySpecificTime
      for (const userItem of data.users) {
        if ((userItem[i as keyof KpisConnectionReportDataUsers] as KpisConnectionReportDataUserIndicator).status) {
          itemHour.numberUsersAtTime++
        }
        itemHour.numberTasks += (userItem[i as keyof KpisConnectionReportDataUsers] as KpisConnectionReportDataUserIndicator).task
      }
      itemHour.averageConnectedUsers = groupIndicator.totalUsers ? +(itemHour.numberUsersAtTime / groupIndicator.totalUsers * 100).toFixed(1) : 0
    }

    return groupIndicator
  }

  /**
   * Gets the number of hours
   * example: 4:15:00 -> 4.25
   */
  getHoursNumberFromTime (time: string) {
    if (!TIME_FORMAT.test(time)) return 0
    const parts = time.split(':')
    // Hour + (Minutes / 60(hours) + Seconds / 60(minutes))
    return Number(parts[0]) + (Number(parts[1]) + Number(parts[2]) / 60) / 60
  }

  formatTextForCsvFile (text: string) {
    text = this.formatNullableTextForCsvFile(text)
    text = this.removeStressFromString(text)
    text = this.removeSpacesFromString(text)
    text = this.removeQuoteFromString(text)
    if (text.includes(',')) {
      return '"' + text + '"'
    } else {
      return text
    }
  }

  formatNullableTextForCsvFile (text: any) {
    if (text === undefined || text === null) {
      return ''
    } else {
      return text
    }
  }

  removeQuoteFromString (text: string): string {
    return text.replace(/"|'/g, '')
  }

  removeSpacesFromString (text: string): string {
    return text.replace(/(?:\r\n|\r|\n)/g, '')
  }

  removeStressFromString (text: string) {
    text = text.replace(/(á)/g, 'a')
    text = text.replace(/(Á)/g, 'A')
    text = text.replace(/(é)/g, 'e')
    text = text.replace(/(É)/g, 'E')
    text = text.replace(/(í)/g, 'i')
    text = text.replace(/(Í)/g, 'I')
    text = text.replace(/(ó)/g, 'o')
    text = text.replace(/(Ó)/g, 'O')
    text = text.replace(/(ú)/g, 'u')
    text = text.replace(/(Ú)/g, 'U')
    return text
  }
}

export default new ReportsService()
